import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { toast } from 'react-toastify';
import { Tools } from '@babylonjs/core/Misc/tools';

import { ContextThreedJson } from './contextThreedJson';
import { IStudioOptions } from '~/Scene/Viewer/interface';
import { _system } from '~/Services/Studio/studioScene';
import { apiSocket } from '~/Api/apiSocket';
import { api } from '~/Api';
// import { DASHBOARD_PAGE } from './contextAuth';
import env from '~/Services/Environment/environment';

const getThumbnailImage = (callback: (image: string) => void) => {
    _system.scene.executeWhenReady(() => {
        //! With big resolutions CreateScreenshot add some empty spaces
        //! if we user hardwareScalinglevel to 0.5
        // Tools.CreateScreenshot(_system.engine, _system.arcCamera
        //! Disable Scissor or screenshot can be black
        //! as CreateScreenshotUsingRenderTarget do a render
        _system.engine.disableScissor();

        const height = 300;
        const width = 425;
        Tools.CreateScreenshot(_system.engine, _system.arcCamera, { width, height }, (image) => {
            //* Keep for image render test purpose
            // let test = el('img', { src: image });
            // mount(document.body, test);
            callback(image);
        }, 'image/jpeg');
    });
};

const redirectToDashboard = (text: string): void => {
    toast.error(`🤷 ${text}, you will be redirected to your dashboard`);
    if (env.saving) {
        /*  setTimeout(() => {
            window.location.href = `${DASHBOARD_PAGE}`;
        }, 5000); */
    }
};

export class ContextProject {
    public contextThreedJson: ContextThreedJson;

    constructor() {
        document.addEventListener('mouseout', (evt) => {
            if (evt.toElement === null && evt.relatedTarget === null) {
                // Make sure project loaded before save when mouseout
                if (this.getProjectId() && this.savingInterval) this.checkSaveNeeded();
            }
        });
    }

    public projectId: string;

    public work(projectId: string, callback: (data) => void): void {
        this.projectId = projectId;
        const id = this.getProjectId();
        // Updates the DB to know that someone is working
        apiSocket.addListener((socket) => {
            socket.emit('project:start', { id }, ({ err, data }) => {
                if (err) {
                    return redirectToDashboard(err);
                }
                this.projectFound(data, callback);
                // callback(data.json);
                return this.startOnlineSaving(30);
            });
        });
    }

    public projectFound(project: IStudioOptions, callback?: (project) => void): void {
        const clonedProject = cloneDeep(project);
        this.contextThreedJson = new ContextThreedJson();
        this.contextThreedJson.setProjectOptions(clonedProject);
        this.contextThreedJson.setColors(clonedProject.colors);
        if (callback) callback(clonedProject);
        if (project && this.getProjectId()) this.startOnlineSaving(30);
    }

    public saveProjectName(name: string): void {
        if (!env.saving) return;
        this.updateProjectData({ name }, (success) => {
            if (!success) {
                toast.error('🤷 Oups, there was an error while saving the new name');
            } else {
                this.contextThreedJson.setProjectOption('name', name);
            }
        });
    }

    public updateProjectData(update, callback?: (success: boolean) => void): void {
        update.id = this.getProjectId();
        api.put('project/update', update, (data) => {
            if (callback) callback(data.success);
        });
    }

    public lastsave: any;

    private savingInterval: any;

    public startOnlineSaving(frequency: number): void {
        this.lastsave = new Date().getTime();
        // this.lastTimeThumbnailSaved = new Date().getTime();

        this.stopOnlineSaving();
        this.savingInterval = setInterval(() => {
            if (document.hasFocus()) {
                if (!this.getProjectId()) {
                    this.stopOnlineSaving();
                    console.error("You can't save project online without a projectId and engine");
                } else {
                    this.save();
                }
            }
        }, frequency * 1000);
    }

    public stopOnlineSaving(): void {
        clearInterval(this.savingInterval);
    }

    protected errorshown = false;

    protected requestSaveOnline(json: any, callback: (data) => void): void {
        if (!json) {
            if (callback) callback(false); // security
        } else if (env.saving) {
            const postJson = JSON.stringify(json);
            apiSocket.getSocket().emit('project:save', { id: this.getProjectId(), json: postJson }, ({ err, data }) => {
                if (err) {
                    console.log('project not saved successfully');
                    this.saved = false;
                    this.checkErrorMessage(false);
                    if (callback) callback(false);
                } else {
                    console.log('project saved successfully');
                    this.saved = true;
                    if (callback) callback(true);
                    if (this.onSaveFunction) this.onSaveFunction();
                }
            });
        }
    }

    public saved = false;

    public saveOnline(json: any, callback: (success: boolean) => void): void {
        if (!this.lastsave && callback) {
            callback(false);
        } else if (!env.saving && callback) {
            callback(false);
        } else {
            // Avoid sending a lot of request when focus is back on window for instance
            // Maximum one save every 5 seconds
            const now = new Date().getTime();
            if (now - this.lastsave < 5000 && callback) {
                callback(this.saved);
            } else {
                this.lastsave = now;
                this.requestSaveOnline(json, callback);
            }
        }
    }

    public checkSaveNeeded(callback?: (success: boolean) => void): void {
        const projectJson = this.contextThreedJson.getAppRoundedJson();
        // Nothing has changed and previus saved has worked
        if (isEqual(projectJson, this.lastExport) && this.saved) {
            if (callback) callback(true);
        } else {
            if (this.onSaveFunction) this.onSaveFunction();
            this.saveOnline(projectJson, callback);
            this.lastExport = cloneDeep(projectJson);
        }
    }

    private save() {
        this.checkSaveNeeded((saved) => {
            this.checkErrorMessage(saved);
        });
    }

    private failednumber = 0;

    public checkErrorMessage(success: boolean): void {
        if (success) this.failednumber = 0;
        else this.failednumber++;

        if (this.failednumber === 2) {
            this.errorshown = true;
            toast.error('We currently have difficulties saving your project 😱, we will try again later 🕵️');
        }
        if (this.failednumber === 5) {
            /* redirectToDashboard('Sorry it seems you have been disconnected'); */
        }
        if (success && this.errorshown) {
            this.errorshown = false;
            toast.success('We manage to save your project again 👨‍🔧, you are good to go!');
        }
    }

    public onSaveFunction: () => void;

    public onSave(onSaveFunction: () => void): void {
        this.onSaveFunction = onSaveFunction;
    }

    public lastExport = {};

    // lastTimeThumbnailSaved = 0;
    public saveThumbnail(callback?: (image) => void): void {
        // if (context === CONTEXT.LOCAL) return;
        // let now = new Date().getTime();
        // // We save the thumbnail maximum every minute
        // if (now - this.lastTimeThumbnailSaved < 60000) {
        //     if (callback) callback();
        //     return;
        // }
        // this.lastTimeThumbnailSaved = now;
        getThumbnailImage((image) => {
            this.uploadImage(image, (res, image) => {
                if (callback) callback(image);
            });
        });
    }

    private uploadImageUrl = 'project/imageupdate';

    private uploadImage(image: string, callback?: (success: boolean, image) => void) {
        if (!env.saving) return;
        const fd = new FormData();
        fd.append('image', image);
        const header = { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'multipart/form-data' };
        const uploadImageUrl = (this.uploadImageUrl) ? this.uploadImageUrl : 'studio/image';
        api.post(uploadImageUrl, { id: this.getProjectId() }, { body: fd, header }, (data) => {
            if (callback) callback(data.success, data.image);
        });
    }

    public isProjectSaved(): boolean {
        return !!(this.projectId);
    }

    public getProjectId = (): string => this.projectId || '6215260d3958e148f0a36d96'
}

export const contextProject = new ContextProject();
